import type { AuthSessionPayload, AuthSubscriptionStatus } from "site/services/auth-session.types";

const WIRE_MAGIC = new Uint8Array([0x58, 0x51, 0x30, 0x31]); const DK_SIZE = 32;

type WireSubscription = {
  bookId: number;
  bookTitle: string;
  status: string;
  accessActiveUntil: string;
  bookContentKeyEnc: { iv: Uint8Array; cipher: Uint8Array } | null;
};

type SessionWire = {
  token: string;
  dk: Uint8Array;
  email: string;
  permissions: string[];
  subscriptions: WireSubscription[];
  lastUpdateStatsMs: number;
  lastUpdateSettingsMs: number;
};

function readI64(view: DataView, offset: { o: number }): number {
  const v = Number(view.getBigInt64(offset.o, false));
  offset.o += 8;
  return v;
}

function readU16(view: DataView, offset: { o: number }): number {
  const v = view.getUint16(offset.o, false);
  offset.o += 2;
  return v;
}

function readBytes(view: DataView, bytes: Uint8Array, offset: { o: number }, len: number): Uint8Array {
  const out = bytes.subarray(offset.o, offset.o + len);
  offset.o += len;
  return out.slice();
}

function readStringU16(view: DataView, bytes: Uint8Array, offset: { o: number }): string {
  const len = readU16(view, offset);
  const raw = readBytes(view, bytes, offset, len);
  return new TextDecoder().decode(raw);
}

function readEncryptedBlob(
  view: DataView,
  bytes: Uint8Array,
  offset: { o: number },
): { iv: Uint8Array; cipher: Uint8Array } | null {
  const flag = bytes[offset.o];
  offset.o += 1;
  if (flag === 0) {
    return null;
  }
  if (flag !== 1) {
    throw new Error("session wire: bad enc flag");
  }
  const ivLen = bytes[offset.o];
  offset.o += 1;
  const iv = readBytes(view, bytes, offset, ivLen);
  const cipherLen = readU16(view, offset);
  const cipher = readBytes(view, bytes, offset, cipherLen);
  return { iv, cipher };
}

function readSubscription(view: DataView, bytes: Uint8Array, offset: { o: number }): WireSubscription {
  const bookId = readI64(view, offset);
  const bookTitle = readStringU16(view, bytes, offset);
  const status = readStringU16(view, bytes, offset);
  const accessActiveUntil = readStringU16(view, bytes, offset);
  const bookContentKeyEnc = readEncryptedBlob(view, bytes, offset);
  return { bookId, bookTitle, status, accessActiveUntil, bookContentKeyEnc };
}

export function decodeSessionWire(bytes: Uint8Array): SessionWire {
  if (bytes.length < 4 || bytes[0] !== WIRE_MAGIC[0] || bytes[1] !== WIRE_MAGIC[1] || bytes[2] !== WIRE_MAGIC[2] || bytes[3] !== WIRE_MAGIC[3]) {
    throw new Error("session wire: bad magic");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const offset = { o: 4 };
  const token = readStringU16(view, bytes, offset);
  const dk = readBytes(view, bytes, offset, DK_SIZE);
  const email = readStringU16(view, bytes, offset);
  const permCount = readU16(view, offset);
  const permissions: string[] = [];
  for (let i = 0; i < permCount; i += 1) {
    permissions.push(readStringU16(view, bytes, offset));
  }
  const subCount = readU16(view, offset);
  const subscriptions: WireSubscription[] = [];
  for (let i = 0; i < subCount; i += 1) {
    subscriptions.push(readSubscription(view, bytes, offset));
  }
  let lastUpdateStatsMs = 0;
  if (offset.o + 8 <= bytes.length) {
    lastUpdateStatsMs = readI64(view, offset);
  }
  let lastUpdateSettingsMs = 0;
  if (offset.o + 8 <= bytes.length) {
    lastUpdateSettingsMs = readI64(view, offset);
  }
  if (offset.o !== bytes.length) {
    throw new Error("session wire: trailing data");
  }
  return { token, dk, email, permissions, subscriptions, lastUpdateStatsMs, lastUpdateSettingsMs };
}

export function wireToAuthSessionPayload(wire: SessionWire): AuthSessionPayload {
  return {
    token: wire.token,
    dk: wire.dk,
    email: wire.email,
    permissions: wire.permissions,
    subscriptions: wire.subscriptions.map((s) => ({
      book_id: s.bookId,
      book_title: s.bookTitle || undefined,
      status: s.status as AuthSubscriptionStatus,
      access_active_until: s.accessActiveUntil || undefined,
      book_content_key_enc: s.bookContentKeyEnc
        ? { iv: s.bookContentKeyEnc.iv, cipher: s.bookContentKeyEnc.cipher }
        : undefined,
    })),
    last_update_stats_ms: wire.lastUpdateStatsMs > 0 ? wire.lastUpdateStatsMs : undefined,
    last_update_settings_ms: wire.lastUpdateSettingsMs > 0 ? wire.lastUpdateSettingsMs : undefined,
  };
}
